import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Droplets, Shield, Zap, Lightbulb, Fence, ShoppingBag } from 'lucide-react';
const services = [{
  icon: Droplets,
  label: 'Drainage System'
}, {
  icon: Shield,
  label: 'Security'
}, {
  icon: Lightbulb,
  label: 'Clean Energy'
}, {
  icon: Zap,
  label: '24/7 Electricity'
}, {
  icon: Fence,
  label: 'Perimeter Fencing'
}, {
  icon: ShoppingBag,
  label: 'Estate Mall'
}];
const ITEM_DURATION = 280; // ms between each card rolling in
const FINAL_HOLD = 500; // hold after last card
interface LoaderProps {
  onFinish: () => void;
}
export function Loader({
  onFinish
}: LoaderProps) {
  const [revealedCount, setRevealedCount] = useState(0);
  useEffect(() => {
    if (revealedCount < services.length) {
      const t = setTimeout(() => setRevealedCount(c => c + 1), ITEM_DURATION);
      return () => clearTimeout(t);
    }
    const final = setTimeout(onFinish, FINAL_HOLD);
    return () => clearTimeout(final);
  }, [revealedCount, onFinish]);
  const progress = revealedCount / services.length * 100;
  return <motion.div initial={{
    opacity: 1
  }} exit={{
    opacity: 0,
    transition: {
      duration: 0.45,
      ease: [0.7, 0, 0.84, 0]
    }
  }} className="fixed inset-0 z-[100] bg-ink flex items-center justify-center overflow-hidden px-6" data-id="element-517">
      {/* Background grain */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle at 1px 1px, #F5B800 1px, transparent 0)',
      backgroundSize: '40px 40px'
    }} data-id="element-518" />

      {/* Decorative rings */}
      <motion.div animate={{
      rotate: 360
    }} transition={{
      duration: 30,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full border border-gold/10 pointer-events-none" data-id="element-519" />
      <motion.div animate={{
      rotate: -360
    }} transition={{
      duration: 45,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] rounded-full border border-gold/5 pointer-events-none" data-id="element-520" />

      <div className="relative w-full max-w-3xl flex flex-col items-center" data-id="element-521">
        {/* Animated Logo */}
        <motion.div initial={{
        scale: 0,
        rotate: -180,
        opacity: 0
      }} animate={{
        scale: 1,
        rotate: 0,
        opacity: 1
      }} transition={{
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative mb-6" data-id="element-522">
          <motion.div animate={{
          scale: [1, 1.3, 1],
          opacity: [0.5, 0, 0.5]
        }} transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-2xl bg-gold/40 blur-2xl" data-id="element-523" />
          <motion.div animate={{
          y: [0, -6, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="relative w-20 h-20 rounded-2xl bg-white p-2 shadow-2xl flex items-center justify-center" data-id="element-524">
            <img src="https://cdn.magicpatterns.com/uploads/6F22E2Ctxndm5LAy6tPVXu/pasted-image.png" alt="Zylus" className="w-full h-full object-contain" data-id="element-525" />
          </motion.div>
        </motion.div>

        {/* Brand text */}
        <motion.h1 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="font-serif text-3xl text-white mb-2" data-id="element-526">
          Zylus Homes
        </motion.h1>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="text-[10px] font-bold tracking-[0.25em] uppercase text-gold mb-10 text-center" data-id="element-527">
          Sophisticated Urban Living Services
        </motion.div>

        {/* Services cards grid — roll in one-by-one */}
        <div className="w-full grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-10" style={{
        perspective: '1200px'
      }} data-id="element-528">
          {services.map((s, i) => {
          const isRevealed = i < revealedCount;
          const Icon = s.icon;
          return <motion.div key={s.label} initial={{
            opacity: 0,
            x: 400,
            rotateY: -75,
            scale: 0.7
          }} animate={isRevealed ? {
            opacity: 1,
            x: 0,
            rotateY: 0,
            scale: 1
          } : {}} transition={{
            duration: 0.7,
            ease: [0.16, 1, 0.3, 1]
          }} style={{
            transformStyle: 'preserve-3d'
          }} className="relative bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-5 flex flex-col items-start gap-3 min-h-[140px] overflow-hidden group" data-id="element-529">
                {/* Subtle inner glow when revealed */}
                {isRevealed && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.3
            }} className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-gold/20 blur-2xl pointer-events-none" data-id="element-530" />}

                {/* Icon */}
                <div className="relative w-10 h-10 rounded-xl bg-gold flex items-center justify-center shadow-lg shadow-gold/20 z-10" data-id="element-531">
                  <Icon className="w-5 h-5 text-ink" strokeWidth={2.2} data-id="element-532" />
                </div>

                {/* Label */}
                <div className="relative z-10" data-id="element-533">
                  <div className="text-white font-medium text-sm leading-tight" data-id="element-534">
                    {s.label}
                  </div>
                  <div className="text-white/40 text-[10px] font-mono uppercase tracking-widest mt-1" data-id="element-535">
                    Available
                  </div>
                </div>

                {/* Check badge in corner */}
                <AnimatePresence data-id="element-536">
                  {isRevealed && <motion.div initial={{
                scale: 0,
                rotate: -45
              }} animate={{
                scale: 1,
                rotate: 0
              }} transition={{
                delay: 0.35,
                type: 'spring',
                stiffness: 320,
                damping: 18
              }} className="absolute top-3 right-3 w-6 h-6 rounded-full bg-gold flex items-center justify-center shadow-md z-10" data-id="element-537">
                      <Check className="w-3.5 h-3.5 text-ink" strokeWidth={3.5} data-id="element-538" />
                    </motion.div>}
                </AnimatePresence>

                {/* Bottom accent line */}
                {isRevealed && <motion.div initial={{
              scaleX: 0
            }} animate={{
              scaleX: 1
            }} transition={{
              delay: 0.4,
              duration: 0.5
            }} className="absolute bottom-0 left-0 right-0 h-[2px] bg-gold origin-left" data-id="element-539" />}
              </motion.div>;
        })}
        </div>

        {/* Progress bar */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6
      }} className="w-full max-w-md" data-id="element-540">
          <div className="flex items-center justify-between mb-2 text-[10px] font-mono uppercase tracking-widest" data-id="element-541">
            <span className="text-white/40" data-id="element-542">Preparing your experience</span>
            <span className="text-gold tabular-nums" data-id="element-543">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="h-[2px] w-full bg-white/10 overflow-hidden rounded-full" data-id="element-544">
            <motion.div animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} className="h-full bg-gold" data-id="element-545" />
          </div>
        </motion.div>
      </div>
    </motion.div>;
}